import { t, Trans } from '@lingui/macro';
import PropTypes from "prop-types";
import React from "react";
import { CSSTransition } from 'react-transition-group';

import getSuggestions from '@api/v3/brandfolders/autosuggest_search';
import { PrimaryButton, TertiaryButton } from '@components/library/button/index';
import { ListDropdown } from '@components/library/dropdown/index';
import { shouldSuggest } from '@components/show_page/sections/section_search/autocompleteTrigger';
import { searchOptions } from '@components/show_page/sections/section_search/helper';
import SearchActions from '@components/show_page/sections/section_search/search_actions';
import { SearchOperatorOptions } from '@components/show_page/sections/section_search/SearchTypes';
import { SuggestionCache } from '@components/show_page/sections/section_search/suggestionCache';
import { UserViewOptionsKeys } from '@components/show_page/sections/view_options/ViewOptionTypes';
import { debounce } from '@helpers/debounce';
import { isAiSearchOn, semanticSearchFeature } from '@helpers/semantic_search';
import { Brandfolder, Collection } from "@helpers/show_page_helpers";

import ToggleDrawerButton from '../labels/toggle_drawer_button';
import SectionsContext from '../sectionsContext';

import Autocomplete from './autocomplete';
import SearchFilterPinsMenu from './search_filter_pins_menu';

import "./styles/search-container.scss";

const PHRASE_STOP_WORDS = new Set([
  // articles / prepositions / conjunctions
  'a', 'an', 'the', 'and', 'or', 'of', 'in', 'on', 'at', 'to', 'by', 'for',
  'with', 'from', 'about', 'as', 'into', 'through',
  // natural language filler / intent words
  'all', 'any', 'some', 'my', 'me', 'us', 'i', 'we', 'that', 'this',
  'are', 'is', 'where', 'what', 'which',
  // domain filler
  'assets', 'show', 'find', 'get', 'give', 'search', 'want', 'need', 'looking',
]);

// Returns the run of words a completion applies to, plus its start index within `text`.
//
// The natural-language framing in front is dropped — "all assets with logo" asks about
// "logo" — but the run then extends to the end of what has been typed, trailing function
// words included. Those are what say the phrase is unfinished: "man on" is reaching for
// "man on palm tree", and cutting the "on" left the endpoint nothing with which to prefer
// that phrase, so it answered with "managing director" instead.
function extractActiveFragment(text) {
  const tokens = [];
  const regex = /\S+/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    tokens.push({ word: match[0], index: match.index });
  }

  const isStopWord = (token) => PHRASE_STOP_WORDS.has(token.word.toLowerCase());

  // Framing ends at the last function word that still has a content word after it. Anything
  // past that point is the phrase being typed, however it happens to end.
  let start = 0;
  for (let i = 0; i < tokens.length; i++) {
    if (isStopWord(tokens[i]) && tokens.slice(i + 1).some((t) => !isStopWord(t))) start = i + 1;
  }

  const active = tokens.slice(start);
  return active.length === 0
    ? { fragment: '', startInText: text.length }
    : { fragment: active.map((t) => t.word).join(' '), startInText: active[0].index };
}

// Suggestions are served from a purpose-built Elasticsearch index in single-digit
// milliseconds, so the old 600ms wait was very nearly all of the perceived latency. This is
// short enough to feel immediate while still collapsing a fast typist's keystrokes.
const SUGGESTION_DEBOUNCE_MS = 150;

class SectionSearch extends React.Component {
  constructor(props) {
    super(props);

    const { searchQuery } = this.props;
    this.state = {
      searchQuery,
      suggestions: [],
      showAutocomplete: false,
      loadingSuggestions: false,
      activeSuggestionId: null,
      // What the visible suggestions were fetched for. Held separately from searchQuery so
      // the emphasis in the dropdown describes the suggestions on screen rather than
      // whatever has been typed in the time since they arrived.
      suggestionQuery: '',
      // The text before the fragment the suggestions complete. Shown ahead of each one so a
      // row reads as the whole query it would search for, not just the word being finished.
      suggestionContext: '',
    };

    // Every keystroke supersedes the last request, and the sequence number is what discards a
    // superseded answer, so a slow earlier response cannot repopulate the dropdown for a
    // fragment the user has moved past.
    //
    // Superseded requests are deliberately left to finish. Cancelling one saves the server
    // nothing — Rails has run the whole action long before the client hangs up — while
    // throwing away an answer that is still perfectly good for the fragment it was asked
    // about, and which the user will type through again on their next attempt at the phrase.
    this.suggestionsControllers = new Set();
    this.latestSuggestionRequest = 0;

    // Answers already received, by fragment. Held on the instance, so it goes away with the
    // page and can never serve one brandfolder's vocabulary to another.
    this.suggestionCache = new SuggestionCache();

    // Enter on a highlighted suggestion is seen twice: the dropdown accepts it on keydown and
    // the input's keyup handler then submits whatever is in the box. Both now run a search, so
    // the second is suppressed to keep one keypress from being recorded as two searches.
    this.suggestionAccepted = false;

    // Phrase-index UX when the org has semantic search AND the user has AI Search on.
    // Toggle off falls back to the structured section:/tag: completions even for
    // semantic_search orgs — vocab jobs still run in the background either way.
    this.nlsAutocomplete = isAiSearchOn();
    this.getSearchSuggestions = debounce(
      (query) => (this.nlsAutocomplete ? this.fetchNlsSuggestions(query) : this.fetchLegacySuggestions(query)),
      this.nlsAutocomplete ? SUGGESTION_DEBOUNCE_MS : 300
    );
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.activeSectionKeys || [];
    const next = this.props.activeSectionKeys || [];
    const sectionsChanged = prev.length !== next.length || prev.some((k, i) => k !== next[i]);
    if (sectionsChanged && this.nlsAutocomplete) {
      this.suggestionCache = new SuggestionCache();
      const { searchQuery } = this.state;
      if (searchQuery) {
        this.getSearchSuggestions(searchQuery);
      }
    }
  }

  componentWillUnmount() {
    this.abortSuggestions();
  }

  // For when the dropdown is going away rather than merely moving on: unmount, or a suggestion
  // accepted. A late answer has nowhere to be put in either case.
  abortSuggestions = () => {
    this.suggestionsControllers.forEach((controller) => {
      if (!controller.signal.aborted) { controller.abort(); }
    });
    this.suggestionsControllers.clear();
  }

  // Moving on, as opposed to going away: answers already asked for are still worth having in
  // the cache, they simply must not reach the dropdown.
  supersedeSuggestions = () => {
    this.latestSuggestionRequest += 1;
    return this.latestSuggestionRequest;
  }

  // The fragment a completion would apply to, and the text it follows. Shared by the immediate
  // cached pass and the debounced request so the two cannot ask about different things.
  //
  // Only the active fragment is ever sent: the suggestions index matches with operator `and`,
  // so the natural-language connectors in "all assets with logo" would otherwise all have to
  // appear in one phrase for it to match at all.
  activeFragment = (searchQuery) => {
    const { fragment, startInText } = extractActiveFragment(searchQuery);
    return { fragment, context: searchQuery.slice(0, startInText) };
  }

  // Runs on the keystroke itself rather than behind the debounce. The debounce exists to avoid
  // spending a request per character, and reading a local map is not a request — waiting 150ms
  // to display an answer already in hand is just a slower dropdown.
  //
  // Returns whether it found one, so the caller need not care how it was answered.
  showCachedSuggestions = (searchQuery) => {
    const { fragment, context } = this.activeFragment(searchQuery);
    if (!shouldSuggest(fragment)) { return false; }

    const cached = this.suggestionCache.get(fragment);
    if (!cached) { return false; }

    // Supersede first: a request in flight for an earlier fragment must not land on top of
    // the answer being put up here.
    this.supersedeSuggestions();
    this.setState({
      loadingSuggestions: false,
      showAutocomplete: true,
      suggestions: cached,
      activeSuggestionId: null,
      suggestionQuery: fragment,
      suggestionContext: context
    });

    return true;
  }

  fetchLegacySuggestions = (searchQuery) => {
    const { brandfolderKey } = this.props;
    const collection = Boolean(Collection.key);

    getSuggestions({
      collection,
      query: searchQuery,
      resourceKey: collection ? Collection.key : brandfolderKey
    }).then((suggestions) => {
      this.setState({
        showAutocomplete: true,
        suggestions
      });
    });
  }

  fetchNlsSuggestions = (searchQuery) => {
    const { fragment: activeToken, context } = this.activeFragment(searchQuery);

    if (!shouldSuggest(activeToken)) {
      this.supersedeSuggestions();
      this.setState({ loadingSuggestions: false, showAutocomplete: false, suggestions: [] });
      return;
    }

    const { brandfolderKey, activeSectionKeys } = this.props;

    const collection = Boolean(Collection.key);

    // Asked again here, not only on the keystroke: an entry can expire in the time the debounce
    // waits, and this is the pass that decides whether to spend a request.
    if (this.showCachedSuggestions(searchQuery)) { return; }

    const requestId = this.supersedeSuggestions();

    // The dropdown holds the previous answer until this one arrives, rather than emptying
    // itself: those rows are a keystroke out of date, but they came from the endpoint, and
    // swapping a usable list for three dots reads as slower than the round trip really is.
    // The fragment and its context are left alone too, so the emphasis keeps describing the
    // rows underneath it, and all three change together when the answer lands.
    this.setState({
      loadingSuggestions: this.state.suggestions.length === 0,
      showAutocomplete: true
    });

    let controller = null;

    getSuggestions({
      collection,
      nls: true,
      query: activeToken,
      resourceKey: collection ? Collection.key : brandfolderKey,
      sectionKeys: activeSectionKeys?.length ? activeSectionKeys : null
    }, (fetchController) => {
      controller = fetchController;
      this.suggestionsControllers.add(fetchController);
    }).then((suggestions) => {
      this.suggestionsControllers.delete(controller);

      // Kept whether or not it is the answer now being shown. It is the endpoint's answer for
      // the fragment it was asked about either way, and typing the same phrase a second time
      // passes back through every one of those fragments.
      this.suggestionCache.set(activeToken, suggestions);

      if (requestId !== this.latestSuggestionRequest) return;

      this.setState({
        loadingSuggestions: false,
        suggestions,
        activeSuggestionId: null,
        suggestionQuery: activeToken,
        suggestionContext: context
      });
    }).catch(() => {
      this.suggestionsControllers.delete(controller);

      if (requestId !== this.latestSuggestionRequest) return;

      this.setState({ loadingSuggestions: false });
    });
  }

  onQueryChange = (searchQuery) => {
    const oldSearchQuery = this.state.searchQuery;
    const initialSearchQuery = this.props.searchQuery;

    // Typing ends the keypress a suggestion was accepted on, so Enter submits normally again.
    this.suggestionAccepted = false;

    // set searchQuery in state so that the input updates as the user types
    this.setState({
      searchQuery
    }, () => {
      // if going from having a previous searchQuery value to blank
      // submit search with blank value to get all results back
      if (oldSearchQuery !== '' && initialSearchQuery !== '' && searchQuery === '' && !this.props.searchPageMode) {
        const { submitSearchQuery } = this.props;
        submitSearchQuery(searchQuery, true);
      } else if (this.nlsAutocomplete) {
        // An answer already in hand goes up now; the debounced pass then either finds the same
        // entry and spends nothing, or goes and asks because it has expired since.
        this.showCachedSuggestions(searchQuery);
        this.getSearchSuggestions(searchQuery);
      } else {
        this.getSearchSuggestions(searchQuery);
      }
    });
  }

  // Accepting a suggestion runs the search. The click is the user saying what they meant, so
  // asking for Enter afterwards makes them commit to the same decision twice, and every search
  // box people are used to submits on select.
  //
  // Submitting is also what records the search and moves focus to the results, neither of which
  // happened while a selection only rewrote the text in the box.
  onSuggestionSelect = (suggestion) => {
    if (!this.nlsAutocomplete) {
      this.setState({ searchQuery: suggestion, showAutocomplete: false });
      return;
    }

    const { submitSearchQuery } = this.props;

    // A suggestion request may still be in flight for the prefix that was being typed.
    this.abortSuggestions();
    this.suggestionAccepted = true;

    this.setState({ searchQuery: suggestion, showAutocomplete: false }, () => {
      submitSearchQuery(suggestion, true);
    });
  }

  getSearchOptions = () => {
    const isNarrow = (this.props.windowDimensions?.innerWidth ?? window.innerWidth) < 580;
    return searchOptions(isNarrow);
  }

  shareSearch = () => {
    BF.Share.shareResource.call();

    if (Collection.key) {
      BF.fx.dataLayerPush({
        collection: BF.fx.collectionSlug(),
        brandfolder: BF.fx.slug(),
        event: "CollectionShare"
      });
    } else {
      BF.fx.dataLayerPush({
        brandfolder: BF.fx.slug(),
        event: "BrandfolderShare"
      });
    }
  };

  onSubmit = (e) => {
    e.preventDefault();
    const { submitSearchQuery } = this.props;
    const { searchQuery, showAutocomplete } = this.state;
    submitSearchQuery(searchQuery, true);
    if (showAutocomplete) {
      this.hideAutoComplete(500);
    }
  }

  hideAutoComplete = (delay = 0) => {
    // use conditional to avoid asnyc when unnecesary (setTimeout will go async even if delay = 0)
    if (delay) {
      setTimeout(() => this.setState({ showAutocomplete: false }), delay);
    } else {
      this.setState({ showAutocomplete: false });
    }
  }

  render() {
    const {
      addRemovePinnedSearch,
      editable,
      hideSearchFilterPins,
      hideSearchOperator,
      loading,
      searchPageMode,
      searchFilters,
      searchSubmitted,
      toggleFiltersDrawer,
      updateUserViewOptions,
      userViewOptions,
    } = this.props;

    const {
      searchQuery,
      suggestions,
      showAutocomplete: autocompleteOpen,
      loadingSuggestions,
      activeSuggestionId,
      suggestionQuery,
      suggestionContext,
    } = this.state;

    const searchDisabled = semanticSearchFeature() ? false : (!searchQuery || searchQuery.length < 1);
    const showAutocomplete = autocompleteOpen && !searchDisabled && (suggestions.length > 0 || loadingSuggestions) && searchQuery.length > 0;

    // Our friends at Cambro aren't fans of the autocomplete, so we'll
    // just disable it for them to make everyone's lives easier.
    const isCambro = BF.fx.organization().key === 'p824zf-2eqqzc-eaeg8';
    const { searchOperator } = userViewOptions;
    const autocompleteVisible = showAutocomplete && !isCambro && searchOperator !== SearchOperatorOptions.Quoted;
    const selectedSearchOperator = this.getSearchOptions().find((option) => option.value === searchOperator);
    const searchOperatorAriaLabel = selectedSearchOperator?.label
      ? t`Search term matching, ${selectedSearchOperator.label}`
      : t`Search term matching`;

    return (
      <SectionsContext.Consumer>
        {({
          labelsDrawerOpen,
          submitSearchQuery
        }) => (
          <div
            className={`search-container${searchPageMode ? ' search-page-mode' : ''}`}
            id="showpage-search-container"
          >
            <div className="filter-button-container">
              {BFG.hasFeature("labels") && (
                <ToggleDrawerButton
                  ariaControls="labels-drawer-panel"
                  ariaExpanded={labelsDrawerOpen}
                  id="labels-drawer-button-section-search"
                  toggleFiltersDrawer={toggleFiltersDrawer}
                >
                  <span className="bff-label" />
                  <p className="drawer-title"><Trans>Labels</Trans></p>
                </ToggleDrawerButton>
              )}
              {!BFG.hasFeature("disable_advanced_filters") && (
                <TertiaryButton
                  aria-controls="advanced-filters-drawer"
                  className="search-filter-toggle advanced-filter-toggle"
                  icon="bff-filters"
                  onClick={() => toggleFiltersDrawer('section-search')}
                >
                  <Trans>Filters</Trans>
                </TertiaryButton>
              )}
              {!hideSearchFilterPins && (editable || searchFilters.length > 0) && (
                <SearchFilterPinsMenu
                  editable={editable}
                  searchFilters={searchFilters}
                  submitSearchQuery={submitSearchQuery}
                />
              )}
            </div>
            <div className={`search-input-container${hideSearchOperator ? ' no-operator' : ''}`}>
              {!hideSearchOperator && (
                <ListDropdown
                  ariaLabel={searchOperatorAriaLabel}
                  className="search-options-dropdown"
                  id="search-options-dropdown"
                  onChange={(option) => {
                    // Track search term matching toggle with Amplitude (Row 132)
                    if (window.BFG?.amplitude?.enabled && window.amplitudeService) {
                      window.amplitudeService.trackEvent('Toggle', {
                        elementName: 'TermMatchingToggle',
                        productArea: 'BrandfolderView',
                        featureArea: 'FilterPanel',
                        eventName: 'Toggle',
                        'key properties': {
                          selection: option.value === SearchOperatorOptions.AND ? 'All' : 'Any'
                        }
                      });
                    }
                    updateUserViewOptions(UserViewOptionsKeys.SearchOperator, option.value);
                  }}
                  options={this.getSearchOptions()}
                  value={searchOperator}
                  virtualizeOptions={false}
                />
              )}
              <div className="input-wrapper">
                <input
                  aria-activedescendant={activeSuggestionId || undefined}
                  aria-autocomplete="list"
                  aria-controls="search-autocomplete-listbox"
                  aria-expanded={autocompleteVisible}
                  aria-label={t`Search`}
                  autoComplete="off"
                  className="input-with-search-magnifier"
                  data-resource-key={Collection.key ? Collection.key : Brandfolder.key}
                  data-resource-type={Collection.key ? t`Collection` : "Brandfolder"}
                  id="brandfolderSearch"
                  maxLength={10000}
                  onBlur={() => this.hideAutoComplete(500)}
                  onChange={({ target }) => this.onQueryChange(target.value)}
                  onKeyUp={(e) => {
                    // onKeyUp is deliberate here, as onKeyDown and onKeyPress will constantly submit and crash the app
                    // when the end users' enter key is held down, stuck, or a cat sat on their keyboard
                    if (e.key === 'Enter') {
                      if (this.suggestionAccepted) {
                        this.suggestionAccepted = false;
                      } else {
                        this.onSubmit(e);
                      }
                    }
                  }}
                  placeholder={t`Search`}
                  role="combobox"
                  type="text"
                  value={searchQuery || ""}
                />
                {searchSubmitted && searchQuery?.length > 0 && (
                  <SearchActions
                    addRemovePinnedSearch={addRemovePinnedSearch}
                    editable={editable}
                    onClearInput={searchPageMode ? () => this.setState({ searchQuery: '' }) : null}
                    searchFilters={searchFilters}
                    searchPageMode={searchPageMode}
                    searchQuery={searchQuery}
                    shareSearch={this.shareSearch}
                    submitSearchQuery={submitSearchQuery}
                  />
                )}
                {autocompleteVisible && (
                  <Autocomplete
                    context={this.nlsAutocomplete ? suggestionContext : ''}
                    id="search-autocomplete-listbox"
                    loading={this.nlsAutocomplete && loadingSuggestions}
                    onActiveChange={(id) => this.setState({ activeSuggestionId: id })}
                    onClose={this.hideAutoComplete}
                    onSelect={this.onSuggestionSelect}
                    query={this.nlsAutocomplete ? suggestionQuery : ''}
                    suggestions={suggestions}
                  />
                )}
              </div>
              <CSSTransition
                classNames="fade-in"
                in={!searchDisabled}
                timeout={300}
                unmountOnExit
              >
                <>
                  <PrimaryButton
                    className="search-button"
                    disabled={searchDisabled}
                    onClick={this.onSubmit}
                    size="small"
                    type="submit"
                  >
                    <Trans>Search</Trans>
                  </PrimaryButton>
                  <button
                    className={`button primary sm search-button-mobile ${searchDisabled ? "disabled" : ""}`}
                    disabled={searchDisabled}
                    onClick={this.onSubmit}
                    type="submit"
                  >
                    <span aria-hidden="true" className="bff-search" />
                  </button>
                </>
              </CSSTransition>
            </div>
          </div>
        )}
      </SectionsContext.Consumer>
    );
  }
}

SectionSearch.propTypes = {
  activeSectionKeys: PropTypes.arrayOf(PropTypes.string),
  addRemovePinnedSearch: PropTypes.func.isRequired,
  brandfolderKey: PropTypes.string.isRequired,
  editable: PropTypes.bool.isRequired,
  hideSearchFilterPins: PropTypes.bool,
  hideSearchOperator: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  searchPageMode: PropTypes.bool,
  searchFilters: PropTypes.arrayOf(PropTypes.shape({})),
  searchSubmitted: PropTypes.bool.isRequired,
  searchQuery: PropTypes.string,
  submitSearchQuery: PropTypes.func.isRequired,
  toggleFiltersDrawer: PropTypes.func.isRequired,
  updateUserViewOptions: PropTypes.func.isRequired,
  userViewOptions: PropTypes.shape({
    searchOperator: PropTypes.string
  }).isRequired,
  windowDimensions: PropTypes.shape({
    innerHeight: PropTypes.number,
    innerWidth: PropTypes.number,
  })
};

SectionSearch.defaultProps = {
  activeSectionKeys: [],
  hideSearchFilterPins: false,
  hideSearchOperator: false,
  searchPageMode: false,
  searchFilters: [],
  searchQuery: '',
  windowDimensions: {},
};

export default SectionSearch;
